import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useDemoModeContext } from './DemoModeContext';

/**
 * Types of sensitive data that can be registered
 */
export type SensitiveDataType =
  | 'clientName'
  | 'contactName'
  | 'projectName'
  | 'email'
  | 'phone'
  | 'address'
  | 'url';

interface SensitiveEntry {
  value: string;
  type: SensitiveDataType;
}

interface SensitiveDataRegistryContextType {
  register: (value: string | null | undefined, type: SensitiveDataType) => void;
  registerMany: (values: (string | null | undefined)[], type: SensitiveDataType) => void;
  getSensitiveStrings: () => Map<string, SensitiveDataType>;
}

const SensitiveDataRegistryContext = createContext<SensitiveDataRegistryContextType | undefined>(
  undefined
);

// Allowed domains that should NOT be masked
const ALLOWED_DOMAINS = ['mediagarcia.com', 'mediagarcia.co'];

// Pattern-based masking for currency, hours, and percentages
// Matches: $1,234 or $1.2K or $500K or $1.5M etc.
const CURRENCY_PATTERN = /\$[\d,]+\.?\d*[KMB]?/gi;
// Matches: "10.5 hrs", "8 hours", "10 / 15 hrs" (time tracking format)
const HOURS_PATTERN = /\b(\d+\.?\d*)\s*(hrs?|hours?)\b/gi;
const STANDALONE_HOURS_PATTERN = /\b(\d+\.?\d*)h\b/gi;
// Matches: "10.5 / 15.0 hrs" or "10.5 / 15.0 hours" (time tracking actual/target format)
const HOURS_RATIO_PATTERN = /\b(\d+\.?\d*)\s*\/\s*(\d+\.?\d*)\s*(hrs?|hours?)\b/gi;
// Match percentages like "45%", "-12.5%", "100%" but avoid matching in URLs
const PERCENT_PATTERN = /(?<![\w/])-?\d+\.?\d*%/g;
// Match standalone decimal numbers that look like hours (X.X format from .toFixed(1))
// Used in summary cards where the number and "hrs" label are in separate DOM elements
const DECIMAL_HOURS_PATTERN = /^\d{1,3}\.\d$/;

/**
 * Check if a string contains an allowed domain
 */
function containsAllowedDomain(str: string): boolean {
  const lower = str.toLowerCase();
  return ALLOWED_DOMAINS.some((domain) => lower.includes(domain));
}

/**
 * Creates a block string of specified length
 */
function blocks(length: number): string {
  return '█'.repeat(Math.max(length, 3));
}

/**
 * Mask a string by replacing each word with block characters
 */
function maskString(str: string): string {
  return str.replace(/\S+/g, (word) => blocks(word.length));
}

/**
 * Mask an email address, preserving structure
 */
function maskEmail(email: string): string {
  const atIndex = email.indexOf('@');
  if (atIndex === -1) return blocks(email.length);

  const localPart = email.substring(0, atIndex);
  const domainPart = email.substring(atIndex + 1);
  const dotIndex = domainPart.lastIndexOf('.');

  if (dotIndex === -1) {
    return `${blocks(localPart.length)}@${blocks(domainPart.length)}`;
  }

  const domainName = domainPart.substring(0, dotIndex);
  const tld = domainPart.substring(dotIndex);

  return `${blocks(localPart.length)}@${blocks(domainName.length)}${tld}`;
}

/**
 * Mask a phone number, preserving formatting
 */
function maskPhone(phone: string): string {
  return phone.replace(/\d/g, 'X');
}

/**
 * Get the appropriate mask for a value based on its type
 */
function getMaskedValue(value: string, type: SensitiveDataType): string {
  switch (type) {
    case 'email':
      return maskEmail(value);
    case 'phone':
      return maskPhone(value);
    default:
      return maskString(value);
  }
}

export function SensitiveDataRegistryProvider({ children }: { children: ReactNode }) {
  const { isDemoMode } = useDemoModeContext();
  const [sensitiveStrings, setSensitiveStrings] = useState<Map<string, SensitiveDataType>>(
    new Map()
  );
  const observerRef = useRef<MutationObserver | null>(null);
  const processingRef = useRef(false);
  // Track demo mode changes to force re-render when disabled
  const [remountKey, setRemountKey] = useState(0);
  const prevDemoModeRef = useRef(isDemoMode);

  // When demo mode is turned OFF, increment key to force remount and restore original values
  useEffect(() => {
    if (prevDemoModeRef.current && !isDemoMode) {
      // Demo mode was just turned off - force remount to restore original values
      setRemountKey((k) => k + 1);
    }
    prevDemoModeRef.current = isDemoMode;
  }, [isDemoMode]);

  const register = useCallback((value: string | null | undefined, type: SensitiveDataType) => {
    if (!value || value.trim().length < 2) return;

    // Don't register allowed domains
    if ((type === 'email' || type === 'url') && containsAllowedDomain(value)) {
      return;
    }

    setSensitiveStrings((prev) => {
      const newMap = new Map(prev);
      newMap.set(value.trim(), type);
      return newMap;
    });
  }, []);

  const registerMany = useCallback(
    (values: (string | null | undefined)[], type: SensitiveDataType) => {
      const validValues = values.filter(
        (v): v is string =>
          !!v &&
          v.trim().length >= 2 &&
          !((type === 'email' || type === 'url') && containsAllowedDomain(v))
      );

      if (validValues.length === 0) return;

      setSensitiveStrings((prev) => {
        const newMap = new Map(prev);
        validValues.forEach((v) => newMap.set(v.trim(), type));
        return newMap;
      });
    },
    []
  );

  const getSensitiveStrings = useCallback(() => sensitiveStrings, [sensitiveStrings]);

  // Process a text node, replacing sensitive strings with masked versions
  const processTextNode = useCallback(
    (node: Text) => {
      if (!isDemoMode) return;

      let text = node.textContent || '';
      let modified = false;

      // 1. Pattern-based masking for currency (always applied in demo mode)
      // Note: We use replace directly and check if text changed, avoiding test() + replace()
      // combination which can have issues with global regex lastIndex
      // Preserve K/M/B suffix if present
      const currencyMasked = text.replace(CURRENCY_PATTERN, (match) => {
        const suffix = match.slice(-1).toUpperCase();
        if (['K', 'M', 'B'].includes(suffix)) {
          return `$XXX${suffix}`;
        }
        return '$XXX,XXX';
      });
      if (currencyMasked !== text) {
        text = currencyMasked;
        modified = true;
      }

      // 2. Pattern-based masking for hours (e.g., "10.5 hrs", "8 hours", "5h")
      // First handle ratio format "10.5 / 15.0 hrs" -> "XX / XX hrs"
      const hoursRatioMasked = text.replace(HOURS_RATIO_PATTERN, 'XX / XX $3');
      if (hoursRatioMasked !== text) {
        text = hoursRatioMasked;
        modified = true;
      }
      // Then handle simple hours format
      const hoursMasked = text.replace(HOURS_PATTERN, 'XX $2');
      if (hoursMasked !== text) {
        text = hoursMasked;
        modified = true;
      }
      const standaloneHoursMasked = text.replace(STANDALONE_HOURS_PATTERN, 'XXh');
      if (standaloneHoursMasked !== text) {
        text = standaloneHoursMasked;
        modified = true;
      }
      // Handle standalone decimal numbers (summary cards where "10.5" is separate from "Actual Hrs")
      if (DECIMAL_HOURS_PATTERN.test(text.trim())) {
        text = 'XX.X';
        modified = true;
      }

      // 3. Pattern-based masking for percentages (profit margins, etc.)
      const percentMasked = text.replace(PERCENT_PATTERN, 'XX%');
      if (percentMasked !== text) {
        text = percentMasked;
        modified = true;
      }

      // 4. String-based masking for registered sensitive values
      if (sensitiveStrings.size > 0) {
        // Sort by length (longest first) to handle overlapping matches correctly
        const sortedEntries = Array.from(sensitiveStrings.entries()).sort(
          (a, b) => b[0].length - a[0].length
        );

        for (const [sensitive, type] of sortedEntries) {
          if (text.includes(sensitive)) {
            const masked = getMaskedValue(sensitive, type);
            text = text.split(sensitive).join(masked);
            modified = true;
          }
        }
      }

      if (modified) {
        node.textContent = text;
      }
    },
    [isDemoMode, sensitiveStrings]
  );

  // Walk the DOM and process all text nodes
  const processAllTextNodes = useCallback(
    (root: Node = document.body) => {
      if (!isDemoMode) return;

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          // Skip script, style, and input elements
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;

          const tagName = parent.tagName.toLowerCase();
          if (
            tagName === 'script' ||
            tagName === 'style' ||
            tagName === 'textarea' ||
            tagName === 'input' ||
            tagName === 'noscript'
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          // Skip if parent has contenteditable
          if (parent.isContentEditable) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const textNodes: Text[] = [];
      let node: Node | null;
      while ((node = walker.nextNode())) {
        textNodes.push(node as Text);
      }

      textNodes.forEach(processTextNode);
    },
    [isDemoMode, sensitiveStrings, processTextNode]
  );

  // Set up MutationObserver to watch for DOM changes
  useEffect(() => {
    if (!isDemoMode) {
      // Clean up observer when demo mode is disabled
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    // Initial processing
    processAllTextNodes();

    // Set up observer for future changes
    observerRef.current = new MutationObserver((mutations) => {
      // Prevent infinite loops from our own modifications
      if (processingRef.current) return;

      processingRef.current = true;

      // Use requestAnimationFrame to batch processing
      requestAnimationFrame(() => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.TEXT_NODE) {
                processTextNode(node as Text);
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                processAllTextNodes(node);
              }
            });
          } else if (mutation.type === 'characterData' && mutation.target.nodeType === Node.TEXT_NODE) {
            processTextNode(mutation.target as Text);
          }
        });

        processingRef.current = false;
      });
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [isDemoMode, processAllTextNodes, processTextNode]);

  // Re-process when sensitive strings change
  useEffect(() => {
    if (isDemoMode && sensitiveStrings.size > 0) {
      // Small delay to let React finish rendering
      const timeout = setTimeout(() => {
        processAllTextNodes();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [isDemoMode, sensitiveStrings, processAllTextNodes]);

  return (
    <SensitiveDataRegistryContext.Provider
      value={{ register, registerMany, getSensitiveStrings }}
    >
      {/* Key forces remount when demo mode is turned off, restoring original values */}
      <div key={remountKey} className="contents">
        {children}
      </div>
    </SensitiveDataRegistryContext.Provider>
  );
}

export function useSensitiveDataRegistry() {
  const context = useContext(SensitiveDataRegistryContext);
  if (context === undefined) {
    throw new Error('useSensitiveDataRegistry must be used within a SensitiveDataRegistryProvider');
  }
  return context;
}
